import type { YearValue } from "@/lib/data/types";
import { cagrOverYears } from "@/lib/stats";

export function latestValue(series: YearValue[]): number | undefined {
  if (series.length === 0) return undefined;
  return [...series].sort((a, b) => a.year - b.year).at(-1)?.value;
}

/**
 * Trailing-12-Monats-Summe aus einzelnen Zahlungsereignissen statt der jaehrlich
 * aggregierten Historie (Runde 5, Punkt 12 - "Yahoos ETF-Datenquelle liefert auch eine
 * Ausschuettungshistorie?"). Live gegen SPY bestaetigt: `historical.dividendPerShare`
 * (die jaehrliche Serie, die getAnnualDividendsPerShare() in yahoo-provider.ts liefert)
 * ist fuer ETFs leer, aber `getRecentDividendPayments()` (einzelne Zahlungsereignisse)
 * liefert echte Daten - ETF-Positionen wuerden dadurch in der langfristigen
 * Dividenden-Einkommens-Hochrechnung sonst stillschweigend uebersprungen. Nur als
 * Fallback gedacht, wenn die jaehrliche Serie leer ist - liefert bewusst KEINE
 * Wachstumsrate (kein mehrjaehriger Jahresvergleich ableitbar aus reinen
 * Einzelereignissen), die Hochrechnung schreibt diese Positionen dann unveraendert fort
 * (bereits bestehendes, ehrliches Verhalten von projectMonthlyDividendIncome()).
 */
export function sumTrailingAnnualDividendPerShare(
  payments: { date: Date; amountPerShare: number }[],
  asOf: Date,
): number | undefined {
  const cutoff = new Date(asOf.getTime() - 365 * 24 * 60 * 60 * 1000);
  const total = payments
    .filter((p) => p.date >= cutoff && p.date <= asOf)
    .reduce((sum, p) => sum + p.amountPerShare, 0);
  return total > 0 ? total : undefined;
}

/** Anzahl aufeinanderfolgender Jahre (bis zum aktuellsten Datenpunkt) ohne Kuerzung. */
export function dividendGrowthStreak(series: YearValue[]): number {
  const sorted = [...series].sort((a, b) => a.year - b.year);
  let streak = 0;
  for (let i = sorted.length - 1; i > 0; i--) {
    if (sorted[i].value >= sorted[i - 1].value && sorted[i].value > 0) streak++;
    else break;
  }
  return streak;
}

/** CAGR der Dividende je Aktie ueber ein N-Jahres-Fenster. */
export const dividendCagr = cagrOverYears;

/**
 * FCF-Ausschuettungsquote der Dividende (backlog.md -> C2 / [Punkt 56], Dividenden-
 * Sicherheits-Kachel): wie viel Prozent des freien Cashflows (gesamt, TTM) als Dividende
 * (Dividende je Aktie x ausstehende Aktien) ausgeschuettet werden. Bis 2026-08-11 als
 * Vielfaches ("1,5x Deckung") ausgedrueckt - auf Eriks Wunsch ([Punkt 56]) auf die dazu
 * inverse Prozent-Formulierung umgestellt ("X % des FCF"), da fuer Nutzer verstaendlicher.
 * Rein faktische Kennzahl - "sicher"/"riskant"-Bewertung bleibt bewusst der aufrufenden
 * UI-Textebene fern (Compliance, siehe C2-Backlog-Punkt). Reine Funktion, kein I/O.
 */
export function computeFcfPayoutRatio(
  freeCashflow: number | undefined,
  latestDividendPerShare: number | undefined,
  sharesOutstanding: number | undefined,
): number | undefined {
  if (
    freeCashflow === undefined ||
    freeCashflow === 0 ||
    latestDividendPerShare === undefined ||
    latestDividendPerShare <= 0 ||
    sharesOutstanding === undefined ||
    sharesOutstanding <= 0
  ) {
    return undefined;
  }
  return (latestDividendPerShare * sharesOutstanding) / freeCashflow;
}

/** Kappt die fuer die 50-Jahre-Hochrechnung verwendete Wachstumsrate, damit ein junger
 * Dividendenzahler mit sehr hoher historischer CAGR (z.B. aus einer winzigen Basis) nicht zu
 * einem astronomischen, unlesbaren Kurvenverlauf fuehrt. Die tatsaechliche historische CAGR
 * bleibt im UI-Text sichtbar - nur die Projektion selbst wird geklemmt. */
export const YIELD_ON_COST_GROWTH_CAP = 0.15;

export interface YieldOnCostProjectionPoint {
  year: number;
  value: number;
}

/**
 * Hochrechnung der "Rendite auf den heutigen Einstiegspreis" (Yield on Cost): wie die
 * Dividendenrendite bezogen auf den HEUTIGEN Kaufpreis steigen wuerde, wenn die
 * Dividende weiter mit `annualGrowthRate` waechst. Reine Extrapolation der Vergangenheit,
 * keine Prognose/Garantie (siehe Disclaimer im UI). `startYear` ist das aktuelle
 * Kalenderjahr, `years` die Anzahl der zu projizierenden Jahre (0 = heute).
 */
export function projectYieldOnCost(
  currentYield: number,
  annualGrowthRate: number,
  years: number,
  startYear: number,
): YieldOnCostProjectionPoint[] {
  const clampedGrowth = Math.min(annualGrowthRate, YIELD_ON_COST_GROWTH_CAP);
  const points: YieldOnCostProjectionPoint[] = [];
  for (let n = 0; n <= years; n++) {
    points.push({
      year: startYear + n,
      value: Math.max(0, currentYield * Math.pow(1 + clampedGrowth, n)),
    });
  }
  return points;
}

export interface DailyDividendYieldPoint {
  date: string;
  yield: number;
}

/**
 * Tagesgenaue Dividendenrendite (Feedback-Runde 4, Punkt 10: "gleiche Granularität wie
 * Bewertung-Tab") - gleiches Stufen-Prinzip wie `computeDailyMultiple()`
 * (lib/valuation/multiples.ts), nur umgekehrtes Verhältnis: dort Kurs÷Kennzahl (KGV/KUV),
 * hier Dividende(gestuft zum Jahresende)÷Kurs. Eigene, kleine Funktion statt Wiederverwendung
 * von computeDailyMultiple mit vertauschten Argumenten, da dessen äußere Schleife über die
 * ERSTE Serie (dort: Kurs) iteriert - fürs selbe Ergebnis müsste weiterhin taeglich über den
 * Kurs iteriert werden, nur mit umgekehrter Division.
 */
export function computeDailyDividendYield(
  priceSeries: { date: string; value: number }[],
  dividendSteps: YearValue[],
): DailyDividendYieldPoint[] {
  const sortedSteps = [...dividendSteps]
    .filter((s) => s.value > 0)
    .map((s) => ({ date: `${s.year}-12-31`, value: s.value }))
    .sort((a, b) => a.date.localeCompare(b.date));
  if (sortedSteps.length === 0) return [];

  const sortedPrices = [...priceSeries].sort((a, b) => a.date.localeCompare(b.date));
  const points: DailyDividendYieldPoint[] = [];
  let stepIndex = -1;
  for (const p of sortedPrices) {
    while (stepIndex + 1 < sortedSteps.length && sortedSteps[stepIndex + 1].date <= p.date) {
      stepIndex++;
    }
    if (stepIndex < 0 || p.value <= 0) continue;
    points.push({ date: p.date, yield: sortedSteps[stepIndex].value / p.value });
  }
  return points;
}

/**
 * Countdown-Text zum naechsten Ex-Dividenden-Tag direkt auf der Aktienseite (backlog.md ->
 * "Ex-Dividenden-Tag-Countdown direkt auf der Aktienseite, nicht nur im separaten
 * Kalender") - undefined fuer ein fehlendes oder bereits vergangenes Datum (kein
 * rueckwirkender Countdown). Tagesgenauer Vergleich (nicht Millisekunden), damit "heute"
 * auch dann korrekt greift, wenn beide Zeitpunkte am selben Kalendertag, aber zu
 * unterschiedlichen Uhrzeiten liegen. Reine Funktion, kein I/O.
 */
export function formatExDividendCountdown(exDividendDate: Date, now: Date = new Date()): string | undefined {
  const startOfDay = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const daysUntil = Math.round((startOfDay(exDividendDate) - startOfDay(now)) / (24 * 60 * 60 * 1000));

  if (daysUntil < 0) return undefined;
  if (daysUntil === 0) return "heute";
  if (daysUntil === 1) return "morgen";
  return `in ${daysUntil} Tagen`;
}

